import type { ChangelogEntry } from "./changelog";
import { emailLayout, emailButton } from "./email/layout";

// =============================================================================
// Génération du HTML de l'email de notification de nouvelle version.
// -----------------------------------------------------------------------------
// Pure function (sans dépendance React/DOM) — réutilisable côté serveur si on
// veut un jour porter le rendu dans une edge function. Pour l'instant on rend
// côté client puis on transmet le HTML à `changelog-broadcast` qui se contente
// d'envoyer via Resend.
//
// Style : email HTML compatible Outlook (table-based, inline styles, palette
// fixe sans variables CSS).
// =============================================================================

interface BuildOptions {
  /** URL absolue de l'app pour le lien "Voir dans la plateforme". */
  appUrl?: string;
}

export function buildChangelogEmail(
  entry: ChangelogEntry,
  opts: BuildOptions = {},
): { subject: string; html: string; text: string } {
  const subject = entry.title
    ? `Aon BRI — Nouvelle version v${entry.version} : ${entry.title}`
    : `Aon BRI — Nouvelle version v${entry.version}`;

  const html = renderHtml(entry, opts.appUrl);
  const text = renderText(entry);

  return { subject, html, text };
}

// =============================================================================
// HTML email (table-based, compatible Outlook)
// =============================================================================

const SECTIONS: Array<{
  key: keyof Pick<ChangelogEntry, "added" | "improved" | "fixed" | "removed">;
  label: string;
  color: string; // bg + text
}> = [
  { key: "added", label: "Ajouté", color: "#16a34a" },
  { key: "improved", label: "Amélioré", color: "#0f6fff" },
  { key: "fixed", label: "Corrigé", color: "#d97706" },
  { key: "removed", label: "Retiré", color: "#dc2626" },
];

function renderHtml(entry: ChangelogEntry, appUrl?: string): string {
  const dateLabel = formatDate(entry.date);
  const sectionsHtml = SECTIONS.map((s) => renderSection(s, entry[s.key])).join("");

  const header =
    `<p style="margin:0;font-size:11px;letter-spacing:.06em;text-transform:uppercase;color:#6b6b76;font-weight:600;">Nouvelle version disponible</p>` +
    `<h1 style="margin:8px 0 4px 0;font-family:Georgia,serif;font-size:26px;line-height:34px;color:#1a1a1a;font-weight:600;">v${escapeHtml(entry.version)}${entry.title ? ` — ${escapeHtml(entry.title)}` : ""}</h1>` +
    `<p style="margin:0 0 16px 0;font-size:13px;color:#6b6b76;">${escapeHtml(dateLabel)}</p>`;

  const summary = entry.summary
    ? `<p style="margin:0 0 20px 0;font-size:15px;line-height:24px;color:#3a3a3a;">${escapeHtml(entry.summary)}</p>`
    : "";

  const sections =
    sectionsHtml ||
    `<p style="margin:0 0 20px 0;color:#6b6b76;font-size:14px;">Aucun détail disponible pour cette version.</p>`;

  const cta = appUrl
    ? emailButton(appUrl.replace(/\/+$/, "") + "/app", "Ouvrir la plateforme")
    : "";

  const preheader = entry.title
    ? `v${entry.version} — ${entry.title}`
    : `Nouvelle version v${entry.version}`;

  return emailLayout(header + summary + sections + cta, { preheader });
}

function renderSection(
  meta: { label: string; color: string },
  items: string[] | undefined,
): string {
  if (!items || items.length === 0) return "";

  const lis = items
    .map(
      (item) => `<li style="margin:0 0 8px 0;font-size:14px;line-height:22px;color:#1a1a1a;">
      ${escapeHtml(item)}
    </li>`,
    )
    .join("");

  return `
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:0 0 20px 0;">
      <tr><td style="padding:0 0 8px 0;">
        <span style="display:inline-block;padding:3px 10px;border-radius:999px;background-color:${meta.color}1a;color:${meta.color};font-size:11px;font-weight:600;letter-spacing:.04em;text-transform:uppercase;">${meta.label}</span>
      </td></tr>
      <tr><td>
        <ul style="margin:0;padding:0 0 0 20px;list-style:disc;">${lis}</ul>
      </td></tr>
    </table>`;
}

// =============================================================================
// Plain-text fallback (utile pour les clients mail texte / accessibilité)
// =============================================================================

function renderText(entry: ChangelogEntry): string {
  const lines: string[] = [];
  lines.push(`Aon BRI — Nouvelle version v${entry.version}`);
  if (entry.title) lines.push(entry.title);
  lines.push(formatDate(entry.date));
  lines.push("");
  if (entry.summary) {
    lines.push(entry.summary);
    lines.push("");
  }
  for (const s of SECTIONS) {
    const items = entry[s.key];
    if (!items || items.length === 0) continue;
    lines.push(`${s.label.toUpperCase()} :`);
    for (const it of items) lines.push(`  • ${it}`);
    lines.push("");
  }
  lines.push("— L'équipe Aon Retraite");
  return lines.join("\n");
}

// =============================================================================
// Utils
// =============================================================================

function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat("fr-FR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function escapeHtml(s: string): string {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
